import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, TextField, Tooltip, Typography,
} from '@mui/material'
import { MdClose, MdDone, MdOpenInNew, MdPrint } from 'react-icons/md'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ConfirmDialog } from '../../Dialog/variants/dialog-confirmation/dialog-confirmation.component'
import { Can } from '../../Can'
import { IFlowManagementCardSelection, IHCPFlowHistory, IHCPFlowKanbanCard } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { canEditMachineNs, canFinishProductionBatch, hasGroupedLogisticsItems, isItemArtApproved, isOrderWithPendingArts } from '../../../Services/HCP/flows/hcp-flow-card.helpers'
import ProductionBatchPanel from './production-batch-panel.component'
import { useAuth } from '../../../Hooks/Auth'
import { canWriteHCPProductionBatches } from '../../../Services/HCP/flows/hcp-flow-permissions.helpers'
import { HCPFlowEnum } from '../../../Services/HCP/flows/hcp-flow.interface'
import api from '../../../Services/api'
import { useToast } from '../../../Hooks/ToasContext'

interface IFlowManagementCardDrawerProps {
  selection: IFlowManagementCardSelection | null
  finishingCardId?: string | null
  onFinishCard?: (card: IHCPFlowKanbanCard) => Promise<void> | void
  onSaveNs?: (card: IHCPFlowKanbanCard, ns: string) => Promise<boolean>
  onClose: () => void
  onBatchChanged?: () => void
}

function formatDate(value?: string | null, withTime = true): string {
  if (!value) return '—'
  const date = parseISO(value)
  if (!isValid(date)) return value
  return format(date, withTime ? "dd/MM/yyyy 'às' HH:mm" : 'dd/MM/yyyy', { locale: ptBR })
}

function formatQuantity(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('pt-BR')
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}
    >
      {children}
    </Typography>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  )
}

function getHistorySequence(history: IHCPFlowHistory): number | null {
  return history.sequence ?? history.statusSequence ?? null
}

const FLOW_LABELS: Record<string, string> = {
  [HCPFlowEnum.AWAITING_ART]: 'Arte',
  [HCPFlowEnum.SHEET]: 'Cartela',
  [HCPFlowEnum.PRODUCTION]: 'Produção',
  [HCPFlowEnum.MACHINE_PRODUCTION]: 'Produção de Máquinas',
  [HCPFlowEnum.LOGISTICS]: 'Logística',
}

function getFlowLabel(flow?: string | null): string {
  if (!flow) return '—'
  return FLOW_LABELS[flow] ?? flow
}

function getSortedHistories(histories?: IHCPFlowHistory[]): IHCPFlowHistory[] {
  return [...(histories ?? [])].sort((a, b) => {
    const displayCodeA = a.displayCode ?? ''
    const displayCodeB = b.displayCode ?? ''

    if (displayCodeA !== displayCodeB) {
      if (!displayCodeA) return 1
      if (!displayCodeB) return -1
      return displayCodeA.localeCompare(displayCodeB, 'pt-BR', { numeric: true })
    }

    const sequenceA = getHistorySequence(a)
    const sequenceB = getHistorySequence(b)

    if (sequenceA !== null && sequenceB !== null && sequenceA !== sequenceB) {
      return sequenceA - sequenceB
    }

    const dateA = a.startedAt ?? a.createdAt ?? ''
    const dateB = b.startedAt ?? b.createdAt ?? ''
    return dateA.localeCompare(dateB)
  })
}

function getCardFlowHistories(selection: IFlowManagementCardSelection | null): IHCPFlowHistory[] {
  const card = selection?.card
  if (!card) return []
  if (card.hcpFlowHistories?.length) return getSortedHistories(card.hcpFlowHistories)

  return [{
    flow: card.hcpCurrentFlow,
    statusName: card.hcpCurrentFlowStatusName ?? selection?.columnName ?? null,
    displayCode: card.displayCode ?? card.hcpCurrentFlowDisplayCode ?? selection?.columnDisplayCode ?? null,
    sequence: card.hcpCurrentFlowStatusSequence ?? selection?.columnSequence ?? null,
    statusSequence: card.hcpCurrentFlowStatusSequence ?? selection?.columnSequence ?? null,
    createdAt: card.hcpCurrentFlowStartedAt ?? card.createdAt ?? null,
    startedAt: card.hcpCurrentFlowStartedAt ?? card.createdAt ?? null,
    deadlineAt: card.hcpCurrentFlowDeadline ?? null,
    finishedAt: card.hcpFinished ? card.updatedAt ?? null : null,
  }]
}

interface IFlowHistoryGroup {
  key: string
  label: string
  histories: IHCPFlowHistory[]
}

function getGroupedItemLabel(
  card: IFlowManagementCardSelection['card'],
  orderProductId: string,
  index: number
): string {
  const groupedItem = card.groupedItems?.find((item) => String(item.id) === orderProductId)
  const prefix = `Item ${index + 1}`

  if (!groupedItem) return `${prefix} - ${orderProductId}`

  return [
    prefix,
    groupedItem.productOrderCode,
    groupedItem.productName,
    groupedItem.nsOrderId ? `Pedido ${groupedItem.nsOrderId}` : null,
  ].filter(Boolean).join(' - ')
}

function getFlowHistoryGroups(selection: IFlowManagementCardSelection | null): IFlowHistoryGroup[] {
  const card = selection?.card
  const histories = getCardFlowHistories(selection)
  if (!card || !histories.length) return []

  if (card.type !== 'HCP_PRE_SERVICE_ORDER') {
    return [{
      key: String(card.id),
      label: 'Item',
      histories,
    }]
  }

  const historiesByOrderProductId = new Map<string, IHCPFlowHistory[]>()

  histories.forEach((history) => {
    const key = history.orderProductId != null ? String(history.orderProductId) : 'sem-item'
    historiesByOrderProductId.set(key, [...(historiesByOrderProductId.get(key) ?? []), history])
  })

  const groupedItemOrder = card.groupedItems?.map((item) => String(item.id)) ?? []
  const orderedKeys = Array.from(historiesByOrderProductId.keys()).sort((a, b) => {
    const indexA = groupedItemOrder.indexOf(a)
    const indexB = groupedItemOrder.indexOf(b)

    if (indexA >= 0 && indexB >= 0) return indexA - indexB
    if (indexA >= 0) return -1
    if (indexB >= 0) return 1
    return a.localeCompare(b, 'pt-BR', { numeric: true })
  })

  return orderedKeys.map((orderProductId, index) => ({
    key: orderProductId,
    label: orderProductId === 'sem-item'
      ? `Item ${index + 1}`
      : getGroupedItemLabel(card, orderProductId, index),
    histories: getSortedHistories(historiesByOrderProductId.get(orderProductId)),
  }))
}

function getHistoryStepTone(history: IHCPFlowHistory, isLast: boolean): { bg: string; border: string; color: string } {
  if (history.skipped) return { bg: '#f4f5f7', border: '#cfd4dc', color: '#667085' }
  if (history.finishedAt) return { bg: '#e8f5e9', border: '#81c784', color: '#2e7d32' }
  if (isLast) return { bg: '#e3f2fd', border: '#64b5f6', color: '#1565c0' }
  return { bg: '#fff8e1', border: '#ffd54f', color: '#a15c00' }
}

function FlowHistoryRuler({ histories }: { histories: IHCPFlowHistory[] }) {
  const orderedHistories = getSortedHistories(histories)

  if (!orderedHistories.length) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {orderedHistories.map((history, index) => {
          const sequence = getHistorySequence(history)
          const tone = getHistoryStepTone(history, index === orderedHistories.length - 1)
          const details = [
            history.days != null ? `${history.days}d` : null,
            history.skipped ? 'Pulou' : history.finishedAt ? 'Finalizado' : index === orderedHistories.length - 1 ? 'Atual' : null,
          ].filter(Boolean).join(' - ')

          return (
            <Box
              key={`${history.id ?? history.displayCode ?? sequence ?? index}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: tone.color,
                    mt: 1.25,
                  }}
                />
                {index < orderedHistories.length - 1 && (
                  <Box sx={{ width: 2, flex: 1, minHeight: 24, bgcolor: 'rgba(0,0,0,0.18)' }} />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 1.5,
                  border: `1px solid ${tone.border}`,
                  bgcolor: tone.bg,
                  px: 1.25,
                  py: 1,
                  mb: index < orderedHistories.length - 1 ? 1 : 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                  <Typography variant="caption" fontWeight={800} sx={{ color: tone.color }}>
                    {[getFlowLabel(history.flow), history.displayCode ?? (sequence != null ? `Seq. ${sequence}` : 'Etapa')]
                      .filter((item) => item && item !== '—')
                      .join(' · ')}
                  </Typography>
                  {details && (
                    <Chip
                      label={details}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.60rem',
                        fontWeight: 700,
                        bgcolor: '#fff',
                        color: tone.color,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  title={history.statusName ?? undefined}
                  sx={{ mt: 0.75, fontSize: '0.78rem', lineHeight: 1.25 }}
                >
                  {history.statusName ?? history.flow ?? 'Movimentacao'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontSize: '0.62rem' }}>
                  Inicio: {formatDate(history.startedAt ?? history.createdAt, false)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                  Prazo: {formatDate(history.deadlineAt, false)}
                </Typography>
                {history.finishedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                    Fim: {formatDate(history.finishedAt, false)}
                  </Typography>
                )}
                {history.skipReason && (
                  <Typography variant="caption" color="text.secondary" title={history.skipReason} noWrap sx={{ display: 'block', fontSize: '0.62rem' }}>
                    {history.skipReason}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function getPreServiceOrderLabel(card: IFlowManagementCardSelection['card']): string {
  const preOs = card.hcpPreServiceOrder
  if (preOs?.externalOsNumber) return `OS ${preOs.externalOsNumber}`
  if (preOs?.number) return `Pré-os ${preOs.number}`
  return `Pré-os ${card.id}`
}

function getPreServiceOrderStatusLabel(status?: string | null): string {
  const labels: Record<string, string> = {
    OPEN: 'Aberta',
    EMITTED: 'Emitida',
    CANCELED: 'Cancelada',
  }

  return status ? labels[status] ?? status : '-'
}

function getReplacementOrderLabel(card: IFlowManagementCardSelection['card']): string {
  const replacementOrder = card.replacementOrder
  if (replacementOrder?.identifier) return `Reposição ${replacementOrder.identifier}`
  if (replacementOrder?.id) return `Reposição ${replacementOrder.id}`
  return `Reposição ${card.id}`
}

function getReplacementOrderStatusLabel(status?: string | null): string {
  const labels: Record<string, string> = {
    OPEN: 'Aberta',
    CLOSED: 'Fechada',
    CANCELED: 'Cancelada',
    STAND_BY: 'Stand-by',
    IN_PRODUCTION: 'Em produção',
    FINISHED: 'Finalizada',
  }

  return status ? labels[status] ?? status : '-'
}

const FlowManagementCardDrawer: FC<IFlowManagementCardDrawerProps> = ({
  selection,
  finishingCardId,
  onFinishCard,
  onSaveNs,
  onClose,
  onBatchChanged,
}) => {
  const { user } = useAuth()
  const [finishConfirmationOpen, setFinishConfirmationOpen] = useState(false)
  const card = selection?.card
  const [ns, setNs] = useState('')
  const [savingNs, setSavingNs] = useState(false)
  const { showToast } = useToast()
  const [printing, setPrinting] = useState(false)
  const printingRef = useRef(false)
  const canPrintOrder = card?.hcpCurrentFlow === HCPFlowEnum.LOGISTICS
    && selection?.columnSequence === 3
    && card.type !== 'HCP_PRE_SERVICE_ORDER'
  const printOrderId = card?.type === 'ORDER' ? card.id : card?.order?.id

  const handlePrintOrder = async () => {
    if (!canPrintOrder || printOrderId == null || printingRef.current) return

    printingRef.current = true
    setPrinting(true)
    try {
      const response = await api.post<Blob>(`/export-relatories/hcp/orders/${encodeURIComponent(String(printOrderId))}`, {}, {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      try {
        link.href = url
        link.download = `pedido-${printOrderId}-${format(new Date(), 'dd-MM-yyyy')}.pdf`
        document.body.appendChild(link)
        link.click()
      } finally {
        link.remove()
        window.URL.revokeObjectURL(url)
      }
      showToast('PDF do pedido baixado com sucesso!', 'success')
    } catch {
      showToast('Erro ao gerar PDF do pedido.', 'error')
    } finally {
      printingRef.current = false
      setPrinting(false)
    }
  }

  useEffect(() => {
    setNs(card?.ns ?? '')
  }, [card?.id, card?.ns])

  const handleSaveNs = async () => {
    if (!card || !canEditMachineNs(card) || !onSaveNs || savingNs) return
    setSavingNs(true)
    try {
      await onSaveNs(card, ns)
    } finally {
      setSavingNs(false)
    }
  }

  const flow = card?.hcpCurrentFlow as HCPFlowEnum | undefined
  const isLogisticsOrderCard = Boolean(card?.isLogisticsOrderCard)
  const showGroupedLogisticsItems = Boolean(card && hasGroupedLogisticsItems(card))
  const isPreServiceOrderCard = card?.type === 'HCP_PRE_SERVICE_ORDER'
  const replacementOrder = card?.replacementOrder ?? null
  const isReplacementOrderCard = Boolean(replacementOrder)
  const isFinishableCard = Boolean(card && canFinishProductionBatch(card, selection?.groupingMode) && canWriteHCPProductionBatches(user))
  const isFinishingCard = Boolean(card && finishingCardId === String(card.id))
  const logisticsItems = card?.type === 'ORDER'
    ? (card.groupedItems ?? []).map((item) => ({
      ...item,
      volumetry: null,
      displayCode: item.hcpCurrentFlowDisplayCode,
      serviceOrderId: null,
    }))
    : card?.logisticsOrderItems ?? []
  const groupedItems = card?.groupedItems ?? []
  const preServiceOrderSummary = [
    `${card?.productionBatchesCount ?? groupedItems.length} lotes`,
    card?.ordersCount != null ? `${card.ordersCount} pedidos` : null,
    card?.customersCount != null ? `${card.customersCount} clientes` : null,
  ].filter(Boolean).join(' - ')
  const showArtChips = flow === HCPFlowEnum.AWAITING_ART || flow === HCPFlowEnum.SHEET || flow === HCPFlowEnum.PRODUCTION || flow === HCPFlowEnum.LOGISTICS
  const itemArtApproved = card ? isItemArtApproved(card, flow) : false
  const orderWithPendingArts = card ? isOrderWithPendingArts(card) : false
  const displayCode = card?.displayCode ?? card?.hcpCurrentFlowDisplayCode
  // Group audit is shown per selected batch below; do not merge siblings by commercial ID.
  const flowHistoryGroups = isPreServiceOrderCard || isLogisticsOrderCard ? [] : getFlowHistoryGroups(selection)
  const statusName = card?.hcpCurrentFlowStatusName ?? selection?.columnName ?? '—'
  const osNumber = card?.hcpPreServiceOrder?.externalOsNumber ?? card?.serviceOrderId ?? null

  const orderTitle = card?.title || (card && isPreServiceOrderCard
    ? getPreServiceOrderLabel(card)
    : card && isReplacementOrderCard
      ? getReplacementOrderLabel(card)
    : card?.order?.nsOrderId
      ? `Pedido ${card.order.nsOrderId}${card.productOrderCode ? ` ${card.productOrderCode}` : ''}`
    : `Item ${card?.id ?? '—'}`)
  const replacementSubtitle = [
    replacementOrder?.store,
    replacementOrder?.brand,
  ].filter(Boolean).join(' - ')

  const handleClose = () => {
    setFinishConfirmationOpen(false)
    onClose()
  }

  const handleConfirmFinish = async () => {
    if (!card || !onFinishCard) return
    await onFinishCard(card)
    setFinishConfirmationOpen(false)
  }

  return (
    <Drawer
      anchor="right"
      open={selection !== null}
      onClose={handleClose}
      PaperProps={{ sx: { width: 440, display: 'flex', flexDirection: 'column' } }}
    >
      {card && (
        <>
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} noWrap title={orderTitle}>
                  {orderTitle}
                </Typography>
                {isPreServiceOrderCard && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {`${getPreServiceOrderStatusLabel(card.hcpPreServiceOrder?.status)} - ${preServiceOrderSummary}`}
                  </Typography>
                )}
                {!isPreServiceOrderCard && (
                <>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  noWrap
                  title={isReplacementOrderCard ? replacementSubtitle || undefined : card.order?.customer?.name ?? undefined}
                >
                  {isReplacementOrderCard ? replacementSubtitle || 'Pedido de reposição' : card.order?.customer?.name ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {card.product?.name ?? '—'}
                </Typography>
                </>
                )}
              </Box>
              <IconButton size="small" onClick={handleClose} sx={{ ml: 1, flexShrink: 0 }}>
                <MdClose size={20} />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
              {showArtChips && !isPreServiceOrderCard && itemArtApproved && (
                <Chip
                  label="Arte aprovada"
                  size="small"
                  sx={{ bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {showArtChips && !isPreServiceOrderCard && !itemArtApproved && (
                <Chip
                  label="Arte pendente"
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {showArtChips && !isPreServiceOrderCard && itemArtApproved && orderWithPendingArts && flow === HCPFlowEnum.SHEET && (
                <Chip
                  label="Outros itens do pedido pendentes"
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {card.hcpFinished && (
                <Chip
                  label="Finalizado no fluxo"
                  size="small"
                  sx={{ bgcolor: 'rgba(0,0,0,0.06)', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {osNumber && (
                <Chip
                  label={`OS #${osNumber}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {isReplacementOrderCard && (
                <Chip
                  label="Reposição"
                  size="small"
                  sx={{ bgcolor: 'rgba(123,31,162,0.10)', color: '#6a1b9a', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {replacementOrder?.status && (
                <Chip
                  label={getReplacementOrderStatusLabel(replacementOrder.status)}
                  size="small"
                  sx={{ bgcolor: 'rgba(0,0,0,0.06)', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {replacementOrder?.productionStatus && (
                <Chip
                  label={getReplacementOrderStatusLabel(replacementOrder.productionStatus)}
                  size="small"
                  sx={{ bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
              {isPreServiceOrderCard && card.hcpPreServiceOrder?.status && (
                <Chip
                  label={getPreServiceOrderStatusLabel(card.hcpPreServiceOrder.status)}
                  size="small"
                  sx={{ bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontWeight: 600, fontSize: '0.70rem' }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {canPrintOrder && (
              <Button
                variant="contained"
                size="small"
                startIcon={printing ? <CircularProgress size={15} color="inherit" /> : <MdPrint size={15} />}
                disabled={printing || printOrderId == null}
                onClick={handlePrintOrder}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
              >
                Imprimir
              </Button>
            )}
            {isPreServiceOrderCard && (
              <>
                <Box>
                  <SectionTitle>Pré-os</SectionTitle>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <InfoItem label="Numero" value={card.hcpPreServiceOrder?.number ? String(card.hcpPreServiceOrder.number) : '-'} />
                    <InfoItem label="OS externa" value={card.hcpPreServiceOrder?.externalOsNumber ?? '-'} />
                    <InfoItem label="Status" value={getPreServiceOrderStatusLabel(card.hcpPreServiceOrder?.status)} />
                    <InfoItem label="Ativa" value={card.hcpPreServiceOrder?.isActive ? 'Sim' : 'Nao'} />
                    <InfoItem label="Emitida em" value={formatDate(card.hcpPreServiceOrder?.emittedAt)} />
                    <InfoItem label="Prazo Pré-os" value={formatDate(card.hcpPreServiceOrder?.currentFlowDeadline)} />
                  </Box>
                </Box>

                <Divider />
              </>
            )}

            <Box>
              <SectionTitle>Status no fluxo</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="Etapa" value={statusName} />
                <InfoItem label="Código" value={displayCode ?? '—'} />
                <InfoItem label="Sequência" value={String(card.hcpCurrentFlowStatusSequence || selection?.columnSequence || '—')} />
                <InfoItem label="Fluxo" value={getFlowLabel(card.hcpCurrentFlow)} />
              </Box>
            </Box>

            <Divider />

            <Box>
              <SectionTitle>{isPreServiceOrderCard ? 'Itens da Pré-os' : showGroupedLogisticsItems ? 'Itens do pedido' : 'Produto'}</SectionTitle>
              {card.groupingWarning && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                  {card.groupingWarning}
                </Typography>
              )}
              {isPreServiceOrderCard ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {groupedItems.map((item) => {
                    const orderLabel = item.nsOrderId
                      ? `Pedido ${item.nsOrderId}`
                      : item.orderId
                        ? `Pedido ${item.orderId}`
                        : null

                    return (
                      <Box
                        key={String(item.id)}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(0,0,0,0.03)',
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                          {item.title ?? `${item.productOrderCode ? `${item.productOrderCode} - ` : ''}${item.productName ?? 'Lote'}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[orderLabel, item.customerName].filter(Boolean).join(' - ') || 'Pedido/cliente nao informados'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[
                            item.totalQuantity != null ? `Qtd ${formatQuantity(item.totalQuantity)}` : null,
                            item.hcpCurrentFlowDisplayCode ? `Status ${item.hcpCurrentFlowDisplayCode}` : null,
                          ].filter(Boolean).join(' - ') || '—'}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              ) : showGroupedLogisticsItems ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {logisticsItems.map((item) => (
                    <Box
                      key={String(item.id)}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,0,0,0.03)',
                        border: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                        {item.title ?? `${item.productOrderCode ? `${item.productOrderCode} · ` : ''}${item.productName ?? 'Lote'}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[
                          item.volumetry,
                          item.totalQuantity != null ? `Qtd ${formatQuantity(item.totalQuantity)}` : null,
                          item.displayCode ? `Status ${item.displayCode}` : null,
                          item.serviceOrderId ? `OS ${item.serviceOrderId}` : null,
                        ].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <InfoItem label="Nome" value={card.product?.name ?? card.groupedItems?.[0]?.productName ?? '—'} />
                  <InfoItem label="Volumetria" value={card.volumetry ?? '—'} />
                  <InfoItem label="Quantidade" value={formatQuantity(card.totalQuantity)} />
                  <InfoItem label="Cód. produto" value={card.product?.code ?? '—'} />
                  <InfoItem label="ID produto" value={card.product?.id ? String(card.product.id) : '—'} />
                </Box>
              )}
            </Box>

            {canEditMachineNs(card) && onSaveNs && canWriteHCPProductionBatches(user) && (
              <Box>
                <SectionTitle>NS</SectionTitle>
                <Box component="form" onSubmit={(event) => { event.preventDefault(); void handleSaveNs() }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="NS do produto"
                    value={ns}
                    onChange={(event) => setNs(event.target.value)}
                    disabled={savingNs}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiInputBase-root': { borderRadius: 1.5, fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }}
                  />
                  <Tooltip title="Salvar NS">
                    <span>
                      <IconButton
                        type="submit"
                        aria-label="Salvar NS"
                        disabled={savingNs || ns.trim() === (card.ns ?? '')}
                        sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32', '&:hover': { bgcolor: 'rgba(46,125,50,0.20)' } }}
                      >
                        {savingNs ? <CircularProgress size={18} color="inherit" /> : <MdDone size={20} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}

            <Divider />

            <Box>
              <SectionTitle>Prazos</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {!isPreServiceOrderCard && (
                  <InfoItem label="Prazo do cliente" value={formatDate(card.customerDeadline)} />
                )}
                {isReplacementOrderCard && (
                  <InfoItem label="Prazo reposição" value={formatDate(replacementOrder?.dueDate)} />
                )}
                <InfoItem label="Prazo do fluxo HCP" value={formatDate(card.hcpCurrentFlowDeadline)} />
                <InfoItem label="DLL" value={formatDate(card.hcpLogisticsDeadline)} />
                <InfoItem label="DCL" value={formatDate(card.hcpCalculatedLogisticsDate)} />
                <InfoItem label="Início no fluxo" value={formatDate(card.hcpCurrentFlowStartedAt)} />
                <InfoItem label="Criado em" value={formatDate(card.createdAt)} />
                <InfoItem label="Atualizado em" value={formatDate(card.updatedAt)} />
              </Box>
            </Box>

            <Divider />

            <ProductionBatchPanel key={`${card.type}:${card.id}`} card={card} onChanged={() => { onBatchChanged?.(); handleClose() }} />

            <Divider />

            <Box sx={{ display: isPreServiceOrderCard ? 'none' : 'block' }}>
              <SectionTitle>{isReplacementOrderCard ? 'Reposição e vínculos' : 'Pedido e vínculos'}</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <InfoItem label="ID do item" value={isPreServiceOrderCard ? 'Card da Pré-os' : isLogisticsOrderCard ? 'Card do pedido' : String(card.orderProductId ?? '—')} />
                <InfoItem label="Posição no pedido" value={card.productOrderCode ?? '—'} />
                {isReplacementOrderCard ? (
                  <>
                    <InfoItem label="ID reposição" value={replacementOrder?.id ? String(replacementOrder.id) : '—'} />
                    <InfoItem label="Identificador" value={replacementOrder?.identifier ?? '—'} />
                    <InfoItem label="Loja" value={replacementOrder?.store ?? '—'} />
                    <InfoItem label="Marca" value={replacementOrder?.brand ?? '—'} />
                    <InfoItem label="Status" value={getReplacementOrderStatusLabel(replacementOrder?.status)} />
                    <InfoItem label="Status produção" value={getReplacementOrderStatusLabel(replacementOrder?.productionStatus)} />
                  </>
                ) : (
                  <>
                    <InfoItem label="ID do pedido" value={card.order?.id ? String(card.order.id) : '—'} />
                    <InfoItem label="Nº pedido Nuvem" value={card.order?.nsOrderId ? String(card.order.nsOrderId) : '—'} />
                    <InfoItem label="E-mail do cliente" value={card.order?.customer?.email ?? '—'} />
                  </>
                )}
                <InfoItem label="OS" value={osNumber ? String(osNumber) : '—'} />
                <InfoItem label="Pré-os" value={card.hcpPreServiceOrder?.number ? String(card.hcpPreServiceOrder.number) : '—'} />
              </Box>
            </Box>

            {card.artLink && (
              <>
                <Divider />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdOpenInNew size={15} />}
                  onClick={() => window.open(card.artLink!, '_blank', 'noopener,noreferrer')}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                >
                  Abrir link da arte
                </Button>
              </>
            )}

            {flowHistoryGroups.length > 0 && (
              <>
                <Divider />
                <Box>
                  <SectionTitle>Régua do fluxo</SectionTitle>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {flowHistoryGroups.map((group) => (
                      <Box key={group.key}>
                        {isPreServiceOrderCard && (
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="text.primary"
                            sx={{ mb: 1, fontSize: '0.82rem' }}
                          >
                            {group.label}
                          </Typography>
                        )}
                        <FlowHistoryRuler histories={group.histories} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {isFinishableCard && onFinishCard && (
              <Can permissions={['feature:hcp-finalizar-card:manage']}>
                <>
                  <Divider />
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={isFinishingCard ? <CircularProgress size={15} color="inherit" /> : <MdDone size={15} />}
                    onClick={() => setFinishConfirmationOpen(true)}
                    disabled={isFinishingCard}
                    sx={{ alignSelf: 'stretch', textTransform: 'none', fontWeight: 700 }}
                  >
                    Finalizar lote
                  </Button>
                </>
              </Can>
            )}
          </Box>

          <ConfirmDialog
            open={finishConfirmationOpen}
            title="Finalizar lote"
            description={`Deseja finalizar ${orderTitle}, com ${formatQuantity(card.quantity ?? card.totalQuantity)} unidades? Os demais lotes permanecem inalterados.`}
            cancelButtonTitle="Não"
            confirmButtonTitle="Sim"
            confirmDisabled={isFinishingCard}
            onCancel={() => setFinishConfirmationOpen(false)}
            onConfirm={handleConfirmFinish}
          />
        </>
      )}
    </Drawer>
  )
}

export default FlowManagementCardDrawer
